import React, { useState, useRef } from 'react';
import { Outlet, useNavigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { useAuth } from '../contexts/AuthContext';
import { catalogAPI } from '../services/api';
import ErrorBoundary from './ErrorBoundary';
import {
  Box,
  Drawer,
  AppBar,
  Toolbar,
  List,
  ListItem,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  IconButton,
  Typography,
  Avatar,
  Menu,
  MenuItem,
  Divider,
  Snackbar,
  Alert,
  useTheme,
  useMediaQuery,
} from '@mui/material';
import {
  Menu as MenuIcon,
  Home as HomeIcon,
  ShoppingBag as ShoppingBagIcon,
  AddShoppingCart as AddShoppingCartIcon,
  Inventory as InventoryIcon,
  People as PeopleIcon,
  Groups as GroupsIcon,
  AdminPanelSettings as AdminIcon,
  History as EventsIcon,
  Lock as LockIcon,
  Logout as LogoutIcon,
  Person as PersonIcon,
  Language as LanguageIcon,
  Download as DownloadIcon,
  UploadFile as UploadFileIcon,
} from '@mui/icons-material';

const drawerWidth = 280;

const Layout = () => {
  const { user, logout, hasRole } = useAuth();
  const { t, i18n } = useTranslation();
  const navigate = useNavigate();
  const location = useLocation();
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down('md'));
  
  const [mobileOpen, setMobileOpen] = useState(false);
  const [anchorEl, setAnchorEl] = useState(null);
  const [langAnchorEl, setLangAnchorEl] = useState(null);
  const [catalogUploading, setCatalogUploading] = useState(false);
  const [snackbar, setSnackbar] = useState({ open: false, message: '', severity: 'success' });
  const catalogInputRef = useRef(null);
  
  const changeLanguage = (lang) => {
    i18n.changeLanguage(lang);
    setLangAnchorEl(null);
  };

  const handleDownloadCatalog = async () => {
    try {
      const response = await catalogAPI.download();
      const url = window.URL.createObjectURL(new Blob([response.data], { type: 'application/pdf' }));
      const link = document.createElement('a');
      link.href = url;
      link.download = 'catalogo.pdf';
      link.click();
      window.URL.revokeObjectURL(url);
    } catch {
      setSnackbar({ open: true, message: t('nav.catalogNotAvailable'), severity: 'warning' });
    }
  };

  const handleUploadCatalog = async (e) => {
    const file = e.target.files?.[0];
    if (!file) return;
    setCatalogUploading(true);
    try {
      await catalogAPI.upload(file);
      setSnackbar({ open: true, message: t('nav.catalogUploadSuccess'), severity: 'success' });
    } catch {
      setSnackbar({ open: true, message: t('nav.catalogUploadError'), severity: 'error' });
    } finally {
      setCatalogUploading(false);
      e.target.value = '';
    }
  };

  const handleDrawerToggle = () => {
    setMobileOpen(!mobileOpen);
  };

  const handleProfileMenuOpen = (event) => {
    setAnchorEl(event.currentTarget);
  };

  const handleProfileMenuClose = () => {
    setAnchorEl(null);
  };

  const handleLogout = () => {
    handleProfileMenuClose();
    logout();
    navigate('/login');
  };

  const handleChangePassword = () => {
    handleProfileMenuClose();
    navigate('/change-password');
  };

  const menuItems = [
    { text: t('nav.home'), icon: <HomeIcon />, path: '/', roles: ['admin', 'operator', 'client'] },
    { text: t('nav.myOrders'), icon: <ShoppingBagIcon />, path: '/orders', roles: ['admin', 'operator', 'client'] },
    { text: t('nav.newOrder'), icon: <AddShoppingCartIcon />, path: '/new-order', roles: ['admin', 'operator', 'client'] },
    { text: t('nav.products'), icon: <InventoryIcon />, path: '/products', roles: ['admin', 'operator'] },
    { text: t('nav.clients'), icon: <PeopleIcon />, path: '/clients', roles: ['admin', 'operator'] },
    { text: t('nav.clientGroups'), icon: <GroupsIcon />, path: '/client-groups', roles: ['admin'] },
    { text: t('nav.users'), icon: <AdminIcon />, path: '/users', roles: ['admin'] },
    { text: t('nav.events'), icon: <EventsIcon />, path: '/events', roles: ['admin'] },
  ];

  const filteredMenuItems = menuItems.filter(item => 
    item.roles.includes(user?.role)
  );

  const drawer = (
    <Box sx={{ height: '100%', display: 'flex', flexDirection: 'column' }}>
      {/* Sidebar Header */}
      <Box sx={{ p: 2, borderBottom: '1px solid rgba(255,255,255,0.1)' }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 2 }}>
          <Avatar 
            sx={{ 
              bgcolor: 'secondary.main',
              width: 40,
              height: 40,
            }}
          >
            <PersonIcon />
          </Avatar>
          <Box>
            <Typography variant="subtitle1" sx={{ fontWeight: 600, color: 'white' }}>
              {t('nav.hello')}, {user?.clientInfo?.companyName || user?.username}
            </Typography>
            <Typography variant="caption" sx={{ color: 'rgba(255,255,255,0.7)', textTransform: 'capitalize' }}>
              {user?.role}
            </Typography>
          </Box>
        </Box>
      </Box>

      {/* Menu Items */}
      <List sx={{ flex: 1, pt: 1 }}>
        {filteredMenuItems.map((item) => {
          const isActive = location.pathname === item.path;
          return (
            <ListItem key={item.text} disablePadding>
              <ListItemButton
                onClick={() => {
                  navigate(item.path);
                  if (isMobile) setMobileOpen(false);
                }}
                sx={{
                  mx: 1,
                  borderRadius: 1,
                  mb: 0.5,
                  bgcolor: isActive ? 'rgba(255,152,0,0.2)' : 'transparent',
                  color: isActive ? 'secondary.main' : 'rgba(255,255,255,0.8)',
                  '&:hover': {
                    bgcolor: isActive ? 'rgba(255,152,0,0.3)' : 'rgba(255,255,255,0.1)',
                  },
                }}
              >
                <ListItemIcon 
                  sx={{ 
                    color: isActive ? 'secondary.main' : 'rgba(255,255,255,0.7)',
                    minWidth: 40,
                  }}
                >
                  {item.icon}
                </ListItemIcon>
                <ListItemText 
                  primary={item.text}
                  primaryTypographyProps={{
                    fontWeight: isActive ? 600 : 400,
                  }}
                />
              </ListItemButton>
            </ListItem>
          );
        })}
      </List>

      {/* Sidebar Footer */}
      <Box sx={{ p: 2, borderTop: '1px solid rgba(255,255,255,0.1)' }}>
        {/* Download Catalog — all roles */}
        <ListItem disablePadding>
          <ListItemButton
            onClick={handleDownloadCatalog}
            sx={{
              borderRadius: 1,
              mb: 0.5,
              color: 'rgba(255,255,255,0.8)',
              '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <DownloadIcon />
            </ListItemIcon>
            <ListItemText primary={t('nav.catalog')} />
          </ListItemButton>
        </ListItem>

        {/* Upload Catalog — admin only */}
        {hasRole('admin') && (
          <ListItem disablePadding>
            <ListItemButton
              onClick={() => catalogInputRef.current?.click()}
              disabled={catalogUploading}
              sx={{
                borderRadius: 1,
                mb: 0.5,
                color: 'rgba(255,255,255,0.8)',
                '&:hover': { bgcolor: 'rgba(255,255,255,0.1)' },
              }}
            >
              <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
                <UploadFileIcon />
              </ListItemIcon>
              <ListItemText
                primary={catalogUploading ? t('nav.catalogUploading') : t('nav.uploadCatalog')}
              />
            </ListItemButton>
            <input
              ref={catalogInputRef}
              type="file"
              accept="application/pdf"
              style={{ display: 'none' }}
              onChange={handleUploadCatalog}
            />
          </ListItem>
        )}

        <Divider sx={{ borderColor: 'rgba(255,255,255,0.1)', my: 0.5 }} />

        <ListItem disablePadding>
          <ListItemButton
            onClick={handleLogout}
            sx={{
              borderRadius: 1,
              color: 'rgba(255,255,255,0.8)',
              '&:hover': {
                bgcolor: 'rgba(255,255,255,0.1)',
              },
            }}
          >
            <ListItemIcon sx={{ color: 'rgba(255,255,255,0.7)', minWidth: 40 }}>
              <LogoutIcon />
            </ListItemIcon>
            <ListItemText primary={t('nav.logout')} />
          </ListItemButton>
        </ListItem>
      </Box>
    </Box>
  );

  return (
    <Box sx={{ display: 'flex', minHeight: '100vh' }}>
      {/* App Bar */}
      <AppBar
        position="fixed"
        sx={{
          width: { md: `calc(100% - ${drawerWidth}px)` },
          ml: { md: `${drawerWidth}px` },
          bgcolor: 'white',
          color: 'text.primary',
          boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
        }}
      >
        <Toolbar>
          <IconButton
            color="inherit"
            aria-label="open drawer"
            edge="start"
            onClick={handleDrawerToggle}
            sx={{ mr: 2, display: { md: 'none' } }}
          >
            <MenuIcon />
          </IconButton>
          
          <Box sx={{ flexGrow: 1, display: 'flex', justifyContent: 'center', alignItems: 'center' }}>
            <Box
              component="img"
              src="/logo.svg"
              alt="Oeste Pan"
              sx={{ height: 40, width: 'auto' }}
            />
          </Box>

          {/* Language Switcher */}
          <IconButton
            onClick={(e) => setLangAnchorEl(e.currentTarget)}
            size="small"
            sx={{ mr: 1 }}
          >
            <LanguageIcon />
          </IconButton>
          <Menu
            anchorEl={langAnchorEl}
            open={Boolean(langAnchorEl)}
            onClose={() => setLangAnchorEl(null)}
          >
            <MenuItem onClick={() => changeLanguage('es')}>Español</MenuItem>
            <MenuItem onClick={() => changeLanguage('en')}>English</MenuItem>
          </Menu>

          <IconButton
            onClick={handleProfileMenuOpen}
            size="small"
            sx={{ ml: 2 }}
          >
            <Avatar sx={{ width: 32, height: 32, bgcolor: 'secondary.main' }}>
              <PersonIcon fontSize="small" />
            </Avatar>
          </IconButton>
        </Toolbar>
      </AppBar>

      {/* Profile Menu */}
      <Menu
        anchorEl={anchorEl}
        open={Boolean(anchorEl)}
        onClose={handleProfileMenuClose}
        transformOrigin={{ horizontal: 'right', vertical: 'top' }}
        anchorOrigin={{ horizontal: 'right', vertical: 'bottom' }}
      >
        <MenuItem onClick={handleChangePassword}>
          <ListItemIcon>
            <LockIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.changePassword')}
        </MenuItem>
        <Divider />
        <MenuItem onClick={handleLogout}>
          <ListItemIcon>
            <LogoutIcon fontSize="small" />
          </ListItemIcon>
          {t('nav.logout')}
        </MenuItem>
      </Menu>

      {/* Sidebar Drawer */}
      <Box
        component="nav"
        sx={{ width: { md: drawerWidth }, flexShrink: { md: 0 } }}
      >
        {/* Mobile Drawer */}
        <Drawer
          variant="temporary"
          open={mobileOpen}
          onClose={handleDrawerToggle}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: 'block', md: 'none' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'primary.main',
              color: 'white',
            },
          }}
        >
          {drawer}
        </Drawer>
        
        {/* Desktop Drawer */}
        <Drawer
          variant="permanent"
          sx={{
            display: { xs: 'none', md: 'block' },
            '& .MuiDrawer-paper': {
              boxSizing: 'border-box',
              width: drawerWidth,
              bgcolor: 'primary.main',
              color: 'white',
              borderRight: 'none',
            },
          }}
          open
        >
          {drawer}
        </Drawer>
      </Box>

      {/* Main Content */}
      <Box
        component="main"
        sx={{
          flexGrow: 1,
          p: 3,
          width: { md: `calc(100% - ${drawerWidth}px)` },
          mt: 8,
          bgcolor: 'background.default',
          minHeight: 'calc(100vh - 64px)',
        }}
      >
        <ErrorBoundary key={location.pathname}>
          <Outlet />
        </ErrorBoundary>
      </Box>

      <Snackbar
        open={snackbar.open}
        autoHideDuration={4000}
        onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert
          onClose={() => setSnackbar((s) => ({ ...s, open: false }))}
          severity={snackbar.severity}
          sx={{ width: '100%' }}
        >
          {snackbar.message}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default Layout;
